import { useState, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";

type LangCode = "en" | "pt";

type LangConfig = {
  code: LangCode;
  label: string;
  iconSrc: string;
};

const asset = (p: string) =>
  `${import.meta.env.BASE_URL}${encodeURI(p.replace(/^\/+/, ""))}`;

const LANGUAGES: LangConfig[] = [
  { code: "en", label: "English", iconSrc: asset("flags/en.png") },
  { code: "pt", label: "Português", iconSrc: asset("flags/pt.png") },
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const defaultLang: LangCode = "pt";

  const current =
    LANGUAGES.find((l) => l.code === (i18n.language as LangCode)) ??
    LANGUAGES.find((l) => l.code === defaultLang)!;

  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChangeLang = (code: LangCode) => {
    i18n.changeLanguage(code);
    if (typeof window !== "undefined") {
      localStorage.setItem("atlante_lang", code);
    }
    handleClose();
  };

  return (
    <Box>
      <IconButton
        onClick={handleOpen}
        sx={{
          borderRadius: "4px",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          width: 48,
          height: 32,
          p: 0,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
          "&:hover": { backgroundColor: "#F7FAFC" },
        }}
      >
        <Box
          component="img"
          src={current.iconSrc}
          alt={current.label}
          sx={{ width: 30, height: 20, borderRadius: 0, objectFit: "cover" }}
        />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            minWidth: 170,
            color: "#1A1A1A",
          },
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === current.code}
            onClick={() => handleChangeLang(lang.code)}
            sx={{
              color: "#1A1A1A",
              "&.Mui-selected": { backgroundColor: "#EDF2F7" },
              "&.Mui-selected:hover": { backgroundColor: "#E2E8F0" },
            }}
          >
            <Box
              component="img"
              src={lang.iconSrc}
              alt={lang.label}
              sx={{
                width: 24,
                height: 16,
                borderRadius: 0,
                objectFit: "cover",
                mr: 1,
              }}
            />
            <Typography sx={{ fontSize: "0.85rem", color: "#1A1A1A" }}>
              {lang.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
